import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Form, Alert, Badge, ProgressBar, Button } from 'react-bootstrap';
import { FaCalendarAlt, FaArrowUp, FaArrowDown, FaChartPie, FaCoins, FaStar, FaHome } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import transactionService from '../services/transactionService';
import categoryService from '../services/categoryService';
import { CATEGORY_TYPES } from '../constants/categoryTypes';

const YearlySummary = () => {
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [availableYears, setAvailableYears] = useState([]);
  const [yearlyData, setYearlyData] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (selectedYear) {
      generateYearlySummary();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, transactions, categories]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [transactionsData, categoriesData] = await Promise.all([
        transactionService.getAll(),
        categoryService.getAll()
      ]);

      setTransactions(transactionsData);
      setCategories(categoriesData);

      // Extrair anos disponíveis das transações
      const years = [...new Set(transactionsData.map(t => t.date.substring(0, 4)))]
        .sort((a, b) => b.localeCompare(a));
      setAvailableYears(years);

      // Definir ano padrão (ano atual ou mais recente)
      if (years.length > 0) {
        setSelectedYear(years[0]);
      }
    } catch (err) {
      setError('Erro ao carregar dados: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const generateYearlySummary = () => {
    if (!selectedYear) return;

    const yearTransactions = transactions.filter(t => 
      t.date.startsWith(selectedYear)
    );

    const monthlyData = generateMonthlyData(yearTransactions);
    const summary = calculateYearlySummary(yearTransactions);
    const categoryAnalysis = calculateCategoryAnalysis(yearTransactions);
    const typeAnalysis = calculateTypeAnalysis(yearTransactions);
    const insights = generateInsights(yearTransactions, monthlyData, categoryAnalysis);

    setYearlyData({
      year: selectedYear,
      summary,
      monthlyData,
      categoryAnalysis,
      typeAnalysis,
      insights,
      transactions: yearTransactions
    });
  };

  const generateMonthlyData = (yearTransactions) => {
    const monthlyTotals = {};
    
    // Inicializar todos os meses do ano
    for (let i = 1; i <= 12; i++) {
      const monthKey = `${selectedYear}-${i.toString().padStart(2, '0')}`;
      monthlyTotals[monthKey] = {
        month: i,
        name: getMonthName(i),
        income: 0,
        expense: 0,
        netResult: 0,
        transactionCount: 0
      };
    }

    yearTransactions.forEach(transaction => {
      const monthKey = transaction.date.substring(0, 7);
      if (monthlyTotals[monthKey]) {
        if (transaction.amount > 0) {
          monthlyTotals[monthKey].income += transaction.amount;
        } else {
          monthlyTotals[monthKey].expense += Math.abs(transaction.amount);
        }
        monthlyTotals[monthKey].transactionCount++;
      }
    });

    // Calcular resultado líquido
    Object.values(monthlyTotals).forEach(month => {
      month.netResult = month.income - month.expense;
    });

    return Object.values(monthlyTotals);
  };

  const calculateYearlySummary = (yearTransactions) => {
    const totalIncome = yearTransactions
      .filter(t => t.amount > 0)
      .reduce((sum, t) => sum + t.amount, 0);
    
    const totalExpense = Math.abs(yearTransactions
      .filter(t => t.amount < 0)
      .reduce((sum, t) => sum + t.amount, 0));

    const netResult = totalIncome - totalExpense;
    const averageMonthlyIncome = totalIncome / 12;
    const averageMonthlyExpense = totalExpense / 12;
    const totalTransactions = yearTransactions.length;

    // Calcular meses com saldo positivo/negativo
    const monthlyData = generateMonthlyData(yearTransactions);
    const positiveMonths = monthlyData.filter(m => m.netResult > 0).length;
    const negativeMonths = monthlyData.filter(m => m.netResult < 0).length;

    return {
      totalIncome,
      totalExpense,
      netResult,
      averageMonthlyIncome,
      averageMonthlyExpense,
      totalTransactions,
      positiveMonths,
      negativeMonths,
      savingsRate: totalIncome > 0 ? (netResult / totalIncome) * 100 : 0
    };
  };

  const calculateCategoryAnalysis = (yearTransactions) => {
    const categoryTotals = {};

    yearTransactions.forEach(transaction => {
      const categoryId = transaction.categoryId;
      if (!categoryTotals[categoryId]) {
        const category = categories.find(c => c.id === categoryId);
        categoryTotals[categoryId] = {
          id: categoryId,
          name: category?.name || 'Categoria não encontrada',
          type: category?.type || 'UNKNOWN',
          total: 0,
          count: 0,
          averageTransaction: 0,
          maxTransaction: 0,
          minTransaction: 0,
          monthlyData: Array(12).fill(0)
        };
      }
      
      categoryTotals[categoryId].total += transaction.amount;
      categoryTotals[categoryId].count++;
      
      // Atualizar máximo e mínimo
      const absAmount = Math.abs(transaction.amount);
      if (categoryTotals[categoryId].maxTransaction < absAmount) {
        categoryTotals[categoryId].maxTransaction = absAmount;
      }
      if (categoryTotals[categoryId].minTransaction === 0 || categoryTotals[categoryId].minTransaction > absAmount) {
        categoryTotals[categoryId].minTransaction = absAmount;
      }

      // Adicionar ao mês específico
      const month = parseInt(transaction.date.substring(5, 7)) - 1;
      categoryTotals[categoryId].monthlyData[month] += transaction.amount;
    });

    // Calcular média de transação
    Object.values(categoryTotals).forEach(cat => {
      cat.averageTransaction = cat.count > 0 ? Math.abs(cat.total) / cat.count : 0;
    });

    return Object.values(categoryTotals)
      .sort((a, b) => Math.abs(b.total) - Math.abs(a.total));
  };

  const calculateTypeAnalysis = (yearTransactions) => {
    const typeGrouping = {};

    Object.entries(CATEGORY_TYPES).forEach(([key, config]) => {
      typeGrouping[key] = {
        type: key,
        label: config.label,
        isExpense: config.isExpense,
        total: 0,
        count: 0,
        categories: [],
        monthlyData: Array(12).fill(0),
        percentage: 0
      };
    });

    yearTransactions.forEach(transaction => {
      const category = categories.find(c => c.id === transaction.categoryId);
      if (category && typeGrouping[category.type]) {
        typeGrouping[category.type].total += transaction.amount;
        typeGrouping[category.type].count++;
        
        const month = parseInt(transaction.date.substring(5, 7)) - 1;
        typeGrouping[category.type].monthlyData[month] += transaction.amount;
      }
    });

    // Calcular percentuais
    const totalExpenses = Object.values(typeGrouping)
      .filter(t => t.isExpense)
      .reduce((sum, t) => sum + Math.abs(t.total), 0);
    
    const totalIncomes = Object.values(typeGrouping)
      .filter(t => !t.isExpense)
      .reduce((sum, t) => sum + t.total, 0);

    Object.values(typeGrouping).forEach(type => {
      if (type.isExpense && totalExpenses > 0) {
        type.percentage = (Math.abs(type.total) / totalExpenses) * 100;
      } else if (!type.isExpense && totalIncomes > 0) {
        type.percentage = (type.total / totalIncomes) * 100;
      }
    });

    return Object.values(typeGrouping)
      .filter(t => t.total !== 0)
      .sort((a, b) => Math.abs(b.total) - Math.abs(a.total));
  };

  const generateInsights = (yearTransactions, monthlyData, categoryAnalysis) => {
    const insights = [];

    // Insight sobre o melhor mês
    const bestMonth = monthlyData.reduce((best, current) => 
      current.netResult > best.netResult ? current : best
    );
    insights.push({
      type: 'success',
      icon: FaArrowUp,
      title: 'Melhor Mês do Ano',
      description: `${bestMonth.name} foi seu melhor mês com resultado líquido de ${formatCurrency(bestMonth.netResult)}`
    });

    // Insight sobre o pior mês
    const worstMonth = monthlyData.reduce((worst, current) => 
      current.netResult < worst.netResult ? current : worst
    );
    insights.push({
      type: 'warning',
      icon: FaArrowDown,
      title: 'Mês Mais Desafiador',
      description: `${worstMonth.name} teve o menor resultado líquido: ${formatCurrency(worstMonth.netResult)}`
    });

    // Insight sobre categoria com mais gastos
    const highestExpenseCategory = categoryAnalysis
      .filter(c => CATEGORY_TYPES[c.type]?.isExpense)
      .sort((a, b) => Math.abs(b.total) - Math.abs(a.total))[0];
    
    if (highestExpenseCategory) {
      insights.push({
        type: 'info',
        icon: FaChartPie,
        title: 'Maior Categoria de Gastos',
        description: `${highestExpenseCategory.name} representou ${formatCurrency(Math.abs(highestExpenseCategory.total))} em gastos anuais`
      });
    }

    // Insight sobre economia média
    const summary = calculateYearlySummary(yearTransactions);
    if (summary.savingsRate > 0) {
      insights.push({
        type: 'success',
        icon: FaCoins,
        title: 'Taxa de Poupança',
        description: `Você conseguiu poupar ${summary.savingsRate.toFixed(1)}% da sua renda anual`
      });
    }

    // Insight sobre consistência
    const consistentMonths = monthlyData.filter(m => m.netResult > 0).length;
    if (consistentMonths >= 8) {
      insights.push({
        type: 'success',
        icon: FaStar,
        title: 'Consistência Financeira',
        description: `Você teve saldo positivo em ${consistentMonths} meses do ano - excelente consistência!`
      });
    }

    return insights;
  };

  const getMonthName = (monthNumber) => {
    const monthNames = [
      'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
      'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
    ];
    return monthNames[monthNumber - 1];
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(amount);
  };

  const getVariantForType = (type) => {
    switch (type) {
      case 'success': return 'success';
      case 'warning': return 'warning';
      case 'info': return 'info';
      default: return 'primary';
    }
  };

  if (loading) {
    return (
      <Container>
        <div className="text-center mt-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Carregando...</span>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h1 className="display-6 mb-3">
                <FaCalendarAlt className="me-3" />
                Resumo Anual
              </h1>
              <p className="text-muted">Análise consolidada dos resultados financeiros anuais</p>
            </div>
            <div>
              <Button as={Link} to="/reports" variant="outline-primary" className="d-flex align-items-center">
                <FaHome className="me-2" />
                Central de Relatórios
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      {error && (
        <Row className="mb-4">
          <Col>
            <Alert variant="danger">{error}</Alert>
          </Col>
        </Row>
      )}

      {/* Seletor de Ano */}
      <Row className="mb-4">
        <Col md={3}>
          <Card>
            <Card.Body>
              <Form.Group>
                <Form.Label><strong>Selecionar Ano</strong></Form.Label>
                <Form.Select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  size="lg"
                >
                  {availableYears.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {yearlyData && (
        <>
          {/* KPIs Principais */}
          <Row className="mb-4">
            <Col md={2}>
              <Card className="text-center h-100">
                <Card.Body>
                  <h6 className="text-muted">Receita Total</h6>
                  <h4 className="text-success">{formatCurrency(yearlyData.summary.totalIncome)}</h4>
                  <small>Média: {formatCurrency(yearlyData.summary.averageMonthlyIncome)}/mês</small>
                </Card.Body>
              </Card>
            </Col>
            <Col md={2}>
              <Card className="text-center h-100">
                <Card.Body>
                  <h6 className="text-muted">Despesa Total</h6>
                  <h4 className="text-danger">{formatCurrency(yearlyData.summary.totalExpense)}</h4>
                  <small>Média: {formatCurrency(yearlyData.summary.averageMonthlyExpense)}/mês</small>
                </Card.Body>
              </Card>
            </Col>
            <Col md={2}>
              <Card className="text-center h-100">
                <Card.Body>
                  <h6 className="text-muted">Resultado Líquido</h6>
                  <h4 className={yearlyData.summary.netResult >= 0 ? 'text-success' : 'text-danger'}>
                    {formatCurrency(yearlyData.summary.netResult)}
                  </h4>
                  <small>Taxa Poupança: {yearlyData.summary.savingsRate.toFixed(1)}%</small>
                </Card.Body>
              </Card>
            </Col>
            <Col md={2}>
              <Card className="text-center h-100">
                <Card.Body>
                  <h6 className="text-muted">Transações</h6>
                  <h4 className="text-primary">{yearlyData.summary.totalTransactions}</h4>
                  <small>Média: {(yearlyData.summary.totalTransactions / 12).toFixed(1)}/mês</small>
                </Card.Body>
              </Card>
            </Col>
            <Col md={2}>
              <Card className="text-center h-100">
                <Card.Body>
                  <h6 className="text-muted">Meses Positivos</h6>
                  <h4 className="text-success">{yearlyData.summary.positiveMonths}</h4>
                  <small>de 12 meses</small>
                </Card.Body>
              </Card>
            </Col>
            <Col md={2}>
              <Card className="text-center h-100">
                <Card.Body>
                  <h6 className="text-muted">Meses Negativos</h6>
                  <h4 className="text-warning">{yearlyData.summary.negativeMonths}</h4>
                  <small>de 12 meses</small>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Insights */}
          <Row className="mb-4">
            <Col>
              <Card>
                <Card.Header>
                  <h5 className="mb-0">Insights do Ano</h5>
                </Card.Header>
                <Card.Body>
                  <Row>
                    {yearlyData.insights.map((insight, index) => {
                      const IconComponent = insight.icon;
                      return (
                        <Col md={6} lg={4} key={index} className="mb-3">
                          <Alert variant={getVariantForType(insight.type)} className="h-100">
                            <div className="d-flex align-items-center mb-2">
                              <IconComponent className="me-2" />
                              <strong>{insight.title}</strong>
                            </div>
                            <p className="mb-0">{insight.description}</p>
                          </Alert>
                        </Col>
                      );
                    })}
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Evolução Mensal */}
          <Row className="mb-4">
            <Col>
              <Card>
                <Card.Header>
                  <h5 className="mb-0">Evolução Mensal</h5>
                </Card.Header>
                <Card.Body>
                  <Table responsive striped>
                    <thead>
                      <tr>
                        <th>Mês</th>
                        <th className="text-end">Receitas</th>
                        <th className="text-end">Despesas</th>
                        <th className="text-end">Resultado</th>
                        <th className="text-center">Transações</th>
                        <th className="text-center">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {yearlyData.monthlyData.map(month => (
                        <tr key={month.month}>
                          <td><strong>{month.name}</strong></td>
                          <td className="text-end text-success">
                            {formatCurrency(month.income)}
                          </td>
                          <td className="text-end text-danger">
                            {formatCurrency(month.expense)}
                          </td>
                          <td className={`text-end ${month.netResult >= 0 ? 'text-success' : 'text-danger'}`}>
                            <strong>{formatCurrency(month.netResult)}</strong>
                          </td>
                          <td className="text-center">
                            {month.transactionCount}
                          </td>
                          <td className="text-center">
                            <Badge bg={month.netResult >= 0 ? 'success' : 'danger'}>
                              {month.netResult >= 0 ? 'Positivo' : 'Negativo'}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Análise por Tipo de Categoria */}
          <Row className="mb-4">
            <Col>
              <Card>
                <Card.Header>
                  <h5 className="mb-0">Distribuição por Tipo de Categoria</h5>
                </Card.Header>
                <Card.Body>
                  {yearlyData.typeAnalysis.map(type => (
                    <div key={type.type} className="mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span>
                          <strong>{type.label}</strong>
                          <small className="text-muted ms-2">
                            ({type.isExpense ? 'Despesa' : 'Receita'})
                          </small>
                        </span>
                        <span>
                          <strong>{formatCurrency(Math.abs(type.total))}</strong>
                          <small className="text-muted ms-2">({type.percentage.toFixed(1)}%)</small>
                        </span>
                      </div>
                      <ProgressBar 
                        variant={type.isExpense ? 'danger' : 'success'} 
                        now={type.percentage} 
                        style={{ height: '8px' }}
                      />
                    </div>
                  ))}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Top Categorias */}
          <Row>
            <Col>
              <Card>
                <Card.Header>
                  <h5 className="mb-0">Top 10 Categorias do Ano</h5>
                </Card.Header>
                <Card.Body>
                  <Table responsive striped>
                    <thead>
                      <tr>
                        <th>Posição</th>
                        <th>Categoria</th>
                        <th>Tipo</th>
                        <th className="text-end">Total</th>
                        <th className="text-center">Transações</th>
                        <th className="text-end">Média por Transação</th>
                      </tr>
                    </thead>
                    <tbody>
                      {yearlyData.categoryAnalysis.slice(0, 10).map((category, index) => (
                        <tr key={category.id}>
                          <td>
                            <Badge bg="primary">{index + 1}º</Badge>
                          </td>
                          <td><strong>{category.name}</strong></td>
                          <td>
                            <Badge bg={CATEGORY_TYPES[category.type]?.isExpense ? 'danger' : 'success'}>
                              {CATEGORY_TYPES[category.type]?.label || category.type}
                            </Badge>
                          </td>
                          <td className="text-end">
                            <strong>{formatCurrency(Math.abs(category.total))}</strong>
                          </td>
                          <td className="text-center">
                            {category.count}
                          </td>
                          <td className="text-end">
                            {formatCurrency(category.averageTransaction)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </Container>
  );
};

export default YearlySummary; 